import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const HEIGHTS = [
    { label: "13'6\"", v: 13.5 },
    { label: "14'0\"", v: 14 },
    { label: "14'6\"", v: 14.5 },
];

const HEIGHT_PENALTY = { 13.5: 0, 14: 15, 14.5: 30 };

export default function Calculator() {
    const [height, setHeight] = useState(13.5);
    const [weight, setWeight] = useState(40000);
    const [axles, setAxles] = useState(5);

    const score = Math.max(
        20,
        Math.min(
            99,
            Math.round(96 - HEIGHT_PENALTY[height] - ((weight - 20000) / 60000) * 20 + (axles - 2) * 4),
        ),
    );
    const status =
        score >= 85
            ? { label: "ROUTE CLEAR", color: "#2E7D32" }
            : score >= 60
              ? { label: "PROCEED W/ CAUTION", color: "#FF8F00" }
              : { label: "RE-ROUTE ADVISED", color: "#B71C1C" };
    const savings = Math.max(
        50,
        Math.round(((80000 - weight) / 1000) * 2 + axles * 12 + (height === 13.5 ? 40 : 0)),
    );

    return (
        <section id="route-specs" data-testid="calculator-section" className="relative px-6 py-24 lg:px-16 lg:py-32">
            <div className="pointer-events-none absolute right-0 top-0 h-[400px] w-[400px] rounded-full bg-[#0055A5]/10 blur-[120px]" />
            <div className="mx-auto max-w-7xl">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                    <p className="font-mono2 text-xs font-bold tracking-[0.35em] text-[#E5A93C]">
                        ROUTE SPECS — LIVE SIMULATOR
                    </p>
                    <h2 className="font-display mt-4 text-4xl uppercase tracking-wide text-[#F6F2E8] sm:text-5xl md:text-6xl">
                        RUN YOUR <span className="text-[#D32F2F]">RIG NUMBERS</span>
                    </h2>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-14 grid grid-cols-1 gap-0 border-2 border-[#E5A93C]/30 bg-[#24211E] shadow-[8px_8px_0px_#0E0D0C] lg:grid-cols-2"
                >
                    <div className="border-b-2 border-[#E5A93C]/30 p-8 lg:border-b-0 lg:border-r-2 lg:p-10">
                        <p className="font-mono2 text-xs font-bold tracking-[0.3em] text-[#C2C7CE]">
                            TRAILER HEIGHT
                        </p>
                        <div className="mt-3 flex gap-3" data-testid="calculator-height-input">
                            {HEIGHTS.map((h) => (
                                <button
                                    key={h.v}
                                    data-testid={`calculator-height-${String(h.v).replace(".", "-")}`}
                                    onClick={() => setHeight(h.v)}
                                    className={`btn-press font-display border-2 border-[#0E0D0C] px-5 py-3 text-lg tracking-widest shadow-[3px_3px_0px_#0E0D0C] ${
                                        height === h.v
                                            ? "bg-[#E5A93C] text-[#161413]"
                                            : "bg-[#161413] text-[#C2C7CE]"
                                    }`}
                                >
                                    {h.label}
                                </button>
                            ))}
                        </div>

                        <p className="font-mono2 mt-10 text-xs font-bold tracking-[0.3em] text-[#C2C7CE]">
                            GROSS WEIGHT —{" "}
                            <span className="text-[#E5A93C]" data-testid="calculator-weight-value">
                                {weight.toLocaleString()} LBS
                            </span>
                        </p>
                        <input
                            type="range"
                            min={20000}
                            max={80000}
                            step={1000}
                            value={weight}
                            onChange={(e) => setWeight(Number(e.target.value))}
                            data-testid="calculator-weight-input"
                            className="mt-4 w-full"
                        />
                        <div className="font-mono2 mt-1 flex justify-between text-[10px] tracking-widest text-[#8C8377]">
                            <span>20,000</span>
                            <span>80,000</span>
                        </div>

                        <p className="font-mono2 mt-10 text-xs font-bold tracking-[0.3em] text-[#C2C7CE]">
                            AXLE COUNT
                        </p>
                        <div className="mt-3 flex gap-2" data-testid="calculator-axles-input">
                            {[2, 3, 4, 5, 6].map((a) => (
                                <button
                                    key={a}
                                    data-testid={`calculator-axles-${a}`}
                                    onClick={() => setAxles(a)}
                                    className={`btn-press font-display h-11 w-11 border-2 border-[#0E0D0C] text-lg shadow-[3px_3px_0px_#0E0D0C] ${
                                        axles === a
                                            ? "bg-[#0055A5] text-[#F6F2E8]"
                                            : "bg-[#161413] text-[#C2C7CE]"
                                    }`}
                                >
                                    {a}
                                </button>
                            ))}
                        </div>

                        <button
                            data-testid="calculator-submit-btn"
                            onClick={() =>
                                toast.success(`ROUTE CHECK COMPLETE — SCORE ${score}/100 — ${status.label}`, {
                                    description: `Estimated diesel savings: $${savings}/mo on compliant routing.`,
                                })
                            }
                            className="btn-press font-display mt-10 w-full border-2 border-[#0E0D0C] bg-[#D32F2F] px-6 py-4 text-xl tracking-widest text-[#F6F2E8] shadow-[4px_4px_0px_#0E0D0C] hover:bg-[#B71C1C]"
                        >
                            RUN ROUTE CHECK
                        </button>
                    </div>

                    <div className="flex flex-col justify-center bg-[#0E0D0C] p-8 lg:p-10">
                        <div className="hazard h-3 w-full" />
                        <p className="font-mono2 mt-8 text-xs font-bold tracking-[0.3em] text-[#8C8377]">
                            CLEARANCE SAFETY SCORE
                        </p>
                        <p
                            className="font-display mt-2 text-8xl leading-none text-[#E5A93C]"
                            data-testid="calculator-score"
                        >
                            {score}
                            <span className="text-3xl text-[#8C8377]">/100</span>
                        </p>
                        <div className="mt-6">
                            <span
                                data-testid="calculator-status"
                                className="font-mono2 inline-block border-2 border-[#0E0D0C] px-4 py-2 text-xs font-bold tracking-[0.25em] text-[#F6F2E8]"
                                style={{ backgroundColor: status.color }}
                            >
                                ● {status.label}
                            </span>
                        </div>
                        <p className="font-mono2 mt-8 text-xs font-bold tracking-[0.3em] text-[#8C8377]">
                            EST. DIESEL SAVINGS
                        </p>
                        <p
                            className="font-display mt-2 text-5xl leading-none text-[#F6F2E8]"
                            data-testid="calculator-savings"
                        >
                            ${savings}
                            <span className="text-xl text-[#8C8377]">/MO</span>
                        </p>
                        <p className="font-type mt-8 text-sm leading-relaxed text-[#C2C7CE]">
                            "Driver, that rig clears every posted bridge on the recommended corridor. Put cha
                            foot in it."
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
